/**
 * Readable text for a failed API call.
 *
 * The backend answers errors as `{loc, type, code, msg}`, and the transport only reports the status
 * code, so "Request failed with status code 422" is what a plain `Error.message` gives the operator.
 * Prefer the server's own text, which arrives under one of two keys.
 *
 * `detail` is read after `msg` because the authorization and token paths answer with that key
 * instead, and they wrap the text in a one-element array — `{"detail": ["Expired token"]}` — so the
 * array is unwrapped rather than shown as its JSON. A 404 carries no body at all, and no amount of
 * key precedence recovers text the server never sent.
 *
 * A request-body rejection is a third shape: the app registers no handler for FastAPI's
 * `RequestValidationError`, so FastAPI's own handler answers, and its entries are objects rather than
 * strings — `{"detail": [{"type": ..., "loc": [...], "msg": "String should have at most 512
 * characters", "input": ...}]}`. Reading only strings dropped those on the floor and showed the
 * transport's status line instead, which is what an operator saw when a too-long note was rejected on
 * the one action that releases wallet-symbol ownership. An entry's own `msg` is read here for that
 * reason.
 */
export const getApiErrorMessage = (error: unknown): string => {
  const data = (error as { response?: { data?: { msg?: unknown; detail?: unknown } } })?.response
    ?.data;
  const detail =
    Array.isArray(data?.detail) && data.detail.length === 1 ? data.detail[0] : data?.detail;
  // An entry may be a string or one of FastAPI's validation objects; take that object's own `msg`.
  const detailText =
    typeof detail === "object" && detail !== null ? (detail as { msg?: unknown }).msg : detail;
  for (const candidate of [data?.msg, detailText]) {
    if (typeof candidate === "string" && candidate.length > 0) return candidate;
  }
  return error instanceof Error ? error.message : String(error);
};

// Safely read the HTTP status off a thrown API (axios) error without an `as unknown as` cast.
export const httpStatusOf = (error: unknown): number | undefined => {
  if (typeof error === "object" && error !== null && "response" in error) {
    return (error as { response?: { status?: number } }).response?.status;
  }
  return undefined;
};

export const isGroupCacheInFlightRefusal = (error: unknown): boolean => {
  if (httpStatusOf(error) !== 409) return false;
  const data = (error as { response?: { data?: unknown } }).response?.data;
  return (
    typeof data === "object" &&
    data !== null &&
    "reason" in data &&
    data.reason === "execution_group_cache_in_flight"
  );
};
